import React, { useState, useEffect } from 'react';
import './MenuManager.css';
import { menuAPI } from '../api/api';
import TableViewer from './TableViewer';

const MenuManager = () => {
  const [allItems, setAllItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [loading, setLoading] = useState(true);

  // Load menu items on component mount
  useEffect(() => {
    loadMenuItems();
  }, []);

  const loadMenuItems = async () => {
    try {
      setLoading(true);
      const response = await menuAPI.getAllMenuItems();
      const items = response.data.items || response.data || [];
      
      setAllItems(items);
      
      // Auto-expand root folders
      const rootFolders = items.filter(item => 
        (item.Folder === 1 || item.Folder === true) && 
        (!item.ParentFolderID || item.ParentFolderID === -1)
      );
      
      setExpandedFolders(new Set(rootFolders.map(f => f.MenuItemID)));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  // Get children of a parent
  const getChildren = (parentID) => {
    return allItems.filter(item => {
      const itemParentID = item.ParentFolderID;
      if (parentID === null) {
        return !itemParentID || itemParentID === -1;
      }
      return itemParentID === parentID;
    });
  };

  // Toggle folder expand/collapse
  const toggleFolder = (menuItemID, e) => {
    e.stopPropagation();
    setExpandedFolders(prev => {
      const updated = new Set(prev);
      if (updated.has(menuItemID)) {
        updated.delete(menuItemID);
      } else {
        updated.add(menuItemID);
      }
      return updated;
    });
  };

  // Handle menu item selection
  const handleSelectItem = (item) => {
    setSelectedItem(item);
    
    // If it's not a folder and has a link record, try to get the table info
    if (!(item.Folder === 1 || item.Folder === true) && item.LinkRecordID) {
      // For now, we'll use the Link field as the table name if available
      if (item.Link) {
        setSelectedTable({
          TableName: item.Link,
          TableID: item.LinkRecordID
        });
      } else {
        setSelectedTable(null);
      }
    } else {
      setSelectedTable(null);
    }
  };

  // Recursively render menu tree
  const renderMenuTree = (parentID = null, level = 0) => {
    const children = getChildren(parentID);
    
    if (children.length === 0) {
      return null;
    }
    
    return (
      <ul className={`menu-tree-list level-${level}`}>
        {children.map(item => {
          const isFolder = item.Folder === 1 || item.Folder === true;
          const isExpanded = expandedFolders.has(item.MenuItemID);
          const childItems = isFolder ? getChildren(item.MenuItemID) : [];
          const hasChildren = childItems.length > 0;
          
          return (
            <li key={item.MenuItemID} className={`menu-item ${isFolder ? 'folder' : 'item'}`}>
              <div className="menu-item-content">
                {isFolder && (
                  <button 
                    className="expand-button"
                    onClick={(e) => toggleFolder(item.MenuItemID, e)}
                  >
                    {isExpanded ? '▼' : '▶'}
                  </button>
                )}
                {!isFolder && <span className="expand-button-placeholder">  </span>}
                
                <span 
                  className={`menu-item-name ${selectedItem?.MenuItemID === item.MenuItemID ? 'selected' : ''}`}
                  onClick={() => handleSelectItem(item)}
                >
                  {isFolder ? '📁' : '📄'} {item.Name}
                </span>
              </div>
              
              {/* Render children if folder is expanded and has children */}
              {isFolder && isExpanded && hasChildren && renderMenuTree(item.MenuItemID, level + 1)}
            </li>
          );
        })}
      </ul>
    );
  };

  if (loading) {
    return <div className="menu-manager-split loading">Loading menu...</div>;
  }

  return (
    <div className="menu-manager-container">
      <div className="menu-manager-split">
        {/* Left Panel: Menu Structure */}
        <div className="menu-left-panel">
          <div className="menu-header">
            <h2>Menu Structure</h2>
            <button className="btn-add-root">+ Root</button>
          </div>
          
          <div className="menu-tree">
            {allItems.length > 0 ? (
              renderMenuTree(null, 0)
            ) : (
              <p className="no-items">No menu items</p>
            )}
          </div>
        </div>

        {/* Right Panel: Table Viewer or Empty State */}
        <div className="menu-right-panel">
          {selectedTable && selectedItem && !(selectedItem.Folder === 1 || selectedItem.Folder === true) ? (
            <div className="table-viewer-wrapper">
              <TableViewer initialTableName={selectedTable.TableName} />
            </div>
          ) : selectedItem ? (
            <div className="empty-state">
              <div className="empty-state-content">
                <p className="empty-title">
                  {(selectedItem.Folder === 1 || selectedItem.Folder === true) ? '📁' : '📄'} {selectedItem.Name}
                </p>
                <p className="empty-text">
                  {(selectedItem.Folder === 1 || selectedItem.Folder === true)
                    ? 'This is a folder. Select a child item to view table data.'
                    : 'This menu item has no linked table.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-content">
                <p className="empty-title">👈 Select a Menu Item</p>
                <p className="empty-text">Choose a menu item from the left panel to view its contents</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MenuManager;
